/**
 * ComplianceGatewayPage.tsx
 *
 * Regulatory compliance gate process page.
 * Links influent samples → effluent samples → discharge gate status.
 *
 * Workflow (per EA / AU EPA / EU UWWTD / US NPDES guidance):
 *  Step 1 — Record influent sample (raw incoming wastewater)
 *  Step 2 — Apply treatment process
 *  Step 3 — Record effluent sample linked to influent
 *  Step 4 — Pass all discharge gate checks
 *  Step 5 — Authorise discharge / consent release
 *
 * All sample pairs are shown with their compliance status. A pair cannot
 * reach "Gate PASS" unless both samples are recorded AND all applicable
 * gate items are marked ✓ in the Discharge Gates tab (EffluentPage).
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShieldCheck, ShieldX, AlertTriangle, CheckCircle2, ChevronDown,
  Droplets, Beaker, ArrowRight, ArrowDown, Link2, LinkIcon,
  FileText, Clock, Info, Plus, Activity, Database, HardDrive,
  Eye, Download, RefreshCw, BookOpen, Flag, Target, Layers,
  Building2, MapPin, CheckSquare, XSquare, List, AlertCircle,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { toast } from 'sonner';
import { getAllLocalSamples, type UnifiedSample } from '@/lib/hybridStorage';
import { useSession } from '@/context/SessionContext';
import { useAuth } from '@/hooks/useAuth';
import { ALL_STANDARDS, type StandardCode } from '@/types/standards';
import { generateTrendReportPDF } from '@/lib/pdfGenerator';

// ─── Discharge gate storage key (mirrors EffluentPage) ───────────────────────
const LS_GATES_KEY = 'rb_discharge_gates_v1';
type GateStatus = 'not_started' | 'in_progress' | 'compliant' | 'non_compliant' | 'waived';

interface StoredGate {
  id: string; title: string; status: GateStatus; notes: string;
  completedAt?: string; operator?: string;
}
interface StoredGateSet { [key: string]: StoredGate[] }

function loadGates(standard: string, customerId?: string | null): StoredGate[] {
  try {
    const all: StoredGateSet = JSON.parse(localStorage.getItem(LS_GATES_KEY) || '{}');
    const k = `${standard}_${customerId || 'global'}`;
    return all[k] || [];
  } catch { return []; }
}

// ─── Workflow step definition ─────────────────────────────────────────────────
const WORKFLOW_STEPS = [
  {
    step: 1,
    title: 'Record Influent Sample',
    icon: Droplets,
    color: 'text-cyan-400',
    bg: 'bg-cyan-500/10',
    border: 'border-cyan-500/30',
    description: 'Collect and analyse raw wastewater entering the treatment plant. Record all required parameters including pH, COD, BOD₅, TSS, NH₃-N, TP, TN, and metals.',
    regulatory: 'Required under EA Condition of Consent / AU EPA Works Approval / EU UWWTD Art.15 / US NPDES 40 CFR 122.44',
    action: '/influent-entry',
    actionLabel: 'Record Influent Sample',
  },
  {
    step: 2,
    title: 'Apply Treatment Process',
    icon: Activity,
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/30',
    description: 'Operate the treatment process (DAF, aeration, filtration, etc.) to reduce contaminants to below discharge consent levels. Monitor process indicators throughout.',
    regulatory: 'Treatment must achieve secondary treatment standards (UWWTD Annex I / EA Consent Schedule 1 / 40 CFR Part 133)',
    action: '/process-train',
    actionLabel: 'View Process Train',
  },
  {
    step: 3,
    title: 'Record Effluent Sample',
    icon: Beaker,
    color: 'text-rb-brand',
    bg: 'bg-rb-brand/10',
    border: 'border-rb-brand/30',
    description: 'After treatment, collect and analyse the treated effluent. Link this sample to the corresponding influent sample for full chain-of-custody traceability.',
    regulatory: 'Chain-of-custody linkage required for EA audit / AU EPA compliance audit / EU reporting under Art.15 / US DMR via NetDMR (40 CFR 122.41)',
    action: '/effluent-entry',
    actionLabel: 'Record Effluent Sample',
  },
  {
    step: 4,
    title: 'Pass Discharge Gate Checks',
    icon: ShieldCheck,
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
    description: 'Complete all regulatory discharge gate checks — permit validity, consent limits verified, sludge management confirmed, monitoring complete, anti-degradation reviewed.',
    regulatory: 'Pre-discharge checklist required before any authorised release. Non-compliant samples require immediate notification to regulatory authority.',
    action: '/effluent',
    actionLabel: 'Open Discharge Gates',
  },
  {
    step: 5,
    title: 'Authorise Discharge',
    icon: Flag,
    color: 'text-violet-400',
    bg: 'bg-violet-500/10',
    border: 'border-violet-500/30',
    description: 'Once all gate checks are PASS/COMPLIANT and effluent parameters are within consent limits, discharge may be authorised. Record operator, date and time of release.',
    regulatory: 'Discharge authorisation must be recorded with operator identity under EA MCERTS / AU EPA Licence Condition / US NPDES Permit Part I. Copies retained ≥5 years.',
    action: '/effluent',
    actionLabel: 'Record Discharge',
  },
];

// ─── Param labels ─────────────────────────────────────────────────────────────
const PARAM_LABELS: Record<string, string> = {
  ph: 'pH', cod: 'COD', bod: 'BOD₅', bod5: 'BOD₅', tss: 'TSS',
  nh3n: 'NH₃-N', tp: 'TP', tn: 'TN', oilgrease: 'O&G', tds: 'TDS',
  temperature: 'Temp', conductivity: 'Cond.', turbidity: 'Turbidity',
  lead: 'Pb', chromium: 'Cr', cadmium: 'Cd', nickel: 'Ni', zinc: 'Zn',
  copper: 'Cu', arsenic: 'As', mercury: 'Hg',
};
function pLabel(k: string): string { return PARAM_LABELS[k.toLowerCase()] || k; }

// ─── Compliance check ─────────────────────────────────────────────────────────
function checkEffluentParam(key: string, value: number, standard: string): { pass: boolean; limit: number | null } | null {
  const std = ALL_STANDARDS.find(s => s.code === standard);
  if (!std?.limits) return null;
  const L = std.limits as any;
  const k = key.toLowerCase();
  if (k === 'ph') {
    const lo = L.phMin ?? 6; const hi = L.phMax ?? 9;
    return { pass: value >= lo && value <= hi, limit: hi };
  }
  const limitMap: Record<string, string> = {
    // Organic / solids
    cod: 'cod', bod: 'bod5', bod5: 'bod5', tss: 'tss',
    // Nutrients
    nh3n: 'ammoniaN', tp: 'totalPhosphorus', tn: 'totalNitrogen',
    // Physical
    oilgrease: 'oilGrease', tds: 'tds',
    temperature: 'temperature', conductivity: 'conductivity',
    // Metals (8 priority)
    lead: 'lead', mercury: 'mercury', cadmium: 'cadmium', chromium: 'chromium',
    arsenic: 'arsenic', nickel: 'nickel', zinc: 'zinc', copper: 'copper',
    // Other regulated parameters
    sulfide: 'sulfides', sulfides: 'sulfides',
    phenol: 'phenol', surfactants: 'surfactants',
    chloride: 'chlorides', chlorides: 'chlorides',
  };
  const lk = limitMap[k];
  if (lk && L[lk] !== undefined) {
    const lim = L[lk];
    return { pass: value <= lim, limit: lim };
  }
  return null;
}

// ─── Sample pair builder ──────────────────────────────────────────────────────
interface SamplePair {
  influent: UnifiedSample;
  effluent: UnifiedSample | null;
  gatesPassed: number;
  gatesTotal: number;
  compliancePct: number | null;
  allParamsPass: boolean | null;
}

function buildPairs(allSamples: UnifiedSample[], customerId: string | null, standard: string): SamplePair[] {
  const influents = allSamples.filter(s =>
    s.sampleType === 'influent' &&
    (!customerId || String(s.customerId) === customerId)
  );
  const effluents = allSamples.filter(s =>
    s.sampleType === 'effluent' &&
    (!customerId || String(s.customerId) === customerId)
  );
  const gates = loadGates(standard, customerId);
  const gatesTotal = gates.length;
  const gatesPassed = gates.filter(g => g.status === 'compliant').length;

  return influents.map(inf => {
    // Find linked effluent
    const eff = effluents.find(e =>
      e.linkedSampleId === inf.id ||
      (e.siteId && inf.siteId && e.siteId === inf.siteId && Math.abs(new Date(e.createdAt).getTime() - new Date(inf.createdAt).getTime()) < 1000 * 60 * 60 * 48)
    ) || null;

    let compliancePct: number | null = null;
    let allParamsPass: boolean | null = null;

    if (eff && Object.keys(eff.params).length > 0) {
      const results = Object.entries(eff.params)
        .map(([k, v]) => checkEffluentParam(k, v, standard))
        .filter(r => r !== null) as { pass: boolean; limit: number | null }[];
      if (results.length > 0) {
        const passing = results.filter(r => r.pass).length;
        compliancePct = Math.round(passing / results.length * 100);
        allParamsPass = results.every(r => r.pass);
      }
    }

    return { influent: inf, effluent: eff, gatesPassed, gatesTotal, compliancePct, allParamsPass };
  });
}

// ─── Status badge ─────────────────────────────────────────────────────────────
function StatusBadge({ pass, label }: { pass: boolean | null; label: string }) {
  if (pass === null) return (
    <span className="flex items-center gap-1 text-[9px] px-2 py-0.5 rounded-full bg-slate-800 text-slate-500 border border-slate-700 font-bold">
      <AlertCircle className="w-2.5 h-2.5" /> {label}
    </span>
  );
  return pass ? (
    <span className="flex items-center gap-1 text-[9px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-bold">
      <CheckCircle2 className="w-2.5 h-2.5" /> {label}
    </span>
  ) : (
    <span className="flex items-center gap-1 text-[9px] px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/20 font-bold">
      <ShieldX className="w-2.5 h-2.5" /> {label}
    </span>
  );
}

// ─── Workflow Step card ───────────────────────────────────────────────────────
function WorkflowStepCard({ step, isLast }: { step: typeof WORKFLOW_STEPS[0]; isLast: boolean }) {
  const navigate = useNavigate();
  const Icon = step.icon;
  return (
    <div className="flex gap-3">
      {/* Step indicator + connector line */}
      <div className="flex flex-col items-center gap-0">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center border shrink-0 ${step.bg} ${step.border}`}>
          <span className={`text-xs font-black ${step.color}`}>{step.step}</span>
        </div>
        {!isLast && <div className="w-px flex-1 bg-slate-800 mt-1 mb-0" style={{ minHeight: 24 }} />}
      </div>

      {/* Card */}
      <div className={`flex-1 mb-3 rounded-xl border bg-rb-surface p-3.5 ${step.border}`}>
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2">
            <Icon className={`w-4 h-4 ${step.color} shrink-0`} />
            <p className="text-[13px] font-bold text-slate-100">{step.title}</p>
          </div>
          <button
            onClick={() => navigate(step.action)}
            className={`flex items-center gap-1 text-[10px] px-2.5 py-1 rounded-lg font-bold shrink-0 transition-all
              ${step.bg} ${step.color} border ${step.border} hover:opacity-80`}
          >
            {step.actionLabel} <ArrowRight className="w-3 h-3" />
          </button>
        </div>
        <p className="text-[11px] text-slate-400 leading-relaxed mb-2">{step.description}</p>
        <div className="flex items-start gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-800/40 border border-slate-800/40">
          <BookOpen className="w-3 h-3 text-slate-500 shrink-0 mt-0.5" />
          <p className="text-[10px] text-slate-500 leading-relaxed">{step.regulatory}</p>
        </div>
      </div>
    </div>
  );
}

// ─── Sample pair card ─────────────────────────────────────────────────────────
function PairCard({ pair, standard }: { pair: SamplePair; standard: string }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const { influent: inf, effluent: eff } = pair;

  const overallPass = pair.allParamsPass === true && pair.gatesPassed === pair.gatesTotal && pair.gatesTotal > 0;
  const overallFail = pair.allParamsPass === false || (pair.gatesTotal > 0 && pair.gatesPassed < pair.gatesTotal);
  const pending = !eff || pair.allParamsPass === null;

  const statusColor = overallPass ? 'border-emerald-500/30 bg-emerald-900/5'
    : overallFail ? 'border-rose-500/30 bg-rose-900/5'
    : 'border-slate-800/50';

  const fmtDate = (d: string) => {
    try { return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return d; }
  };

  return (
    <div className={`rounded-xl border ${statusColor} bg-rb-surface overflow-hidden`}>
      {/* Header */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-800/20 transition-colors"
        onClick={() => setExpanded(e => !e)}
      >
        {/* Gate indicator */}
        <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${
          overallPass ? 'bg-emerald-400' : overallFail ? 'bg-rose-400' : 'bg-amber-400 animate-pulse'
        }`} />

        {/* Summary */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[12px] font-bold text-slate-100 truncate">{inf.sampleName}</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded font-mono bg-slate-800 text-slate-500">{inf.reportNumber || `ID:${inf.id}`}</span>
            {inf.standard && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-500 font-mono">{inf.standard}</span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
            <span className="text-[10px] text-slate-500 flex items-center gap-1">
              <Clock className="w-2.5 h-2.5" /> {fmtDate(inf.createdAt)}
            </span>
            {eff ? (
              <span className="text-[9px] flex items-center gap-1 text-emerald-400">
                <Link2 className="w-2.5 h-2.5" /> Effluent linked
              </span>
            ) : (
              <span className="text-[9px] flex items-center gap-1 text-amber-400">
                <AlertTriangle className="w-2.5 h-2.5" /> No effluent sample
              </span>
            )}
          </div>
        </div>

        {/* Quick badges */}
        <div className="flex items-center gap-1.5 shrink-0">
          <StatusBadge
            pass={pair.allParamsPass}
            label={pair.allParamsPass === null ? 'Pending' : pair.allParamsPass ? 'Compliant' : 'Non-compliant'}
          />
          {pair.gatesTotal > 0 && (
            <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold border ${
              pair.gatesPassed === pair.gatesTotal
                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
            }`}>
              Gates {pair.gatesPassed}/{pair.gatesTotal}
            </span>
          )}
          <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* Expanded detail */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="border-t border-slate-800/60 px-4 py-4 space-y-4 bg-[#071220]">

              {/* Influent → Effluent chain */}
              <div className="grid sm:grid-cols-2 gap-3">
                {/* Influent card */}
                <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Droplets className="w-3.5 h-3.5 text-cyan-400" />
                    <p className="text-[10px] font-bold text-cyan-400 uppercase tracking-wide">Influent Sample</p>
                    {inf.dbConfirmed ? (
                      <span className="text-[8px] px-1 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center gap-0.5">
                        <Database className="w-2 h-2" /> DB
                      </span>
                    ) : (
                      <span className="text-[8px] px-1 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 flex items-center gap-0.5">
                        <HardDrive className="w-2 h-2" /> Local
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] font-semibold text-slate-200 mb-1">{inf.sampleName}</p>
                  <p className="text-[10px] text-slate-500 mb-2">{fmtDate(inf.createdAt)}</p>
                  {/* Key params */}
                  <div className="grid grid-cols-2 gap-1">
                    {Object.entries(inf.params).slice(0, 6).map(([k, v]) => (
                      <div key={k} className="flex justify-between text-[9px]">
                        <span className="text-slate-600">{pLabel(k)}</span>
                        <span className="text-slate-300 font-mono">{typeof v === 'number' ? v.toFixed(1) : v}</span>
                      </div>
                    ))}
                    {Object.keys(inf.params).length > 6 && (
                      <p className="text-[9px] text-slate-600 col-span-2">+{Object.keys(inf.params).length - 6} more params</p>
                    )}
                  </div>
                </div>

                {/* Effluent card */}
                {eff ? (
                  <div className="rounded-xl border border-rb-brand/20 bg-rb-brand/5 p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Beaker className="w-3.5 h-3.5 text-rb-brand" />
                      <p className="text-[10px] font-bold text-rb-brand uppercase tracking-wide">Effluent Sample</p>
                      {eff.dbConfirmed ? (
                        <span className="text-[8px] px-1 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center gap-0.5">
                          <Database className="w-2 h-2" /> DB
                        </span>
                      ) : (
                        <span className="text-[8px] px-1 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 flex items-center gap-0.5">
                          <HardDrive className="w-2 h-2" /> Local
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] font-semibold text-slate-200 mb-1">{eff.sampleName}</p>
                    <p className="text-[10px] text-slate-500 mb-2">{fmtDate(eff.createdAt)}</p>
                    {/* Param compliance */}
                    <div className="grid grid-cols-2 gap-1">
                      {Object.entries(eff.params).slice(0, 6).map(([k, v]) => {
                        const c = checkEffluentParam(k, v, standard);
                        return (
                          <div key={k} className="flex justify-between text-[9px]">
                            <span className="text-slate-600">{pLabel(k)}</span>
                            <span className={`font-mono font-bold ${
                              c === null ? 'text-slate-400' : c.pass ? 'text-emerald-400' : 'text-rose-400'
                            }`}>{typeof v === 'number' ? v.toFixed(1) : v}</span>
                          </div>
                        );
                      })}
                    </div>
                    {pair.compliancePct !== null && (
                      <div className="mt-2 pt-2 border-t border-rb-brand/10">
                        <div className="flex justify-between text-[9px] mb-1">
                          <span className="text-slate-500">Compliance</span>
                          <span className={`font-bold ${pair.compliancePct === 100 ? 'text-emerald-400' : pair.compliancePct >= 80 ? 'text-amber-400' : 'text-rose-400'}`}>
                            {pair.compliancePct}%
                          </span>
                        </div>
                        <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${pair.compliancePct === 100 ? 'bg-emerald-500' : pair.compliancePct >= 80 ? 'bg-amber-500' : 'bg-rose-500'}`}
                            style={{ width: `${pair.compliancePct}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3 flex flex-col items-center justify-center gap-2">
                    <AlertTriangle className="w-6 h-6 text-amber-500/60" />
                    <p className="text-[10px] font-bold text-amber-400 text-center">Effluent sample not yet recorded</p>
                    <p className="text-[9px] text-slate-600 text-center">An effluent sample linked to this influent is required before discharge gates can be fully checked</p>
                    <button
                      onClick={() => navigate('/effluent-entry')}
                      className="mt-1 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold bg-rb-brand/10 text-rb-brand border border-rb-brand/20 hover:bg-rb-brand/20 transition-all"
                    >
                      <Plus className="w-3 h-3" /> Record Effluent Sample
                    </button>
                  </div>
                )}
              </div>

              {/* Gate summary */}
              {pair.gatesTotal > 0 && (
                <div className="rounded-xl border border-slate-800/40 bg-slate-800/20 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                      <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                      <p className="text-[10px] font-bold text-slate-300">Discharge Gate Status</p>
                    </div>
                    <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold border ${
                      pair.gatesPassed === pair.gatesTotal
                        ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                        : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                    }`}>
                      {pair.gatesPassed} / {pair.gatesTotal} gates compliant
                    </span>
                  </div>
                  <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        pair.gatesPassed === pair.gatesTotal ? 'bg-emerald-500' : 'bg-amber-500'
                      }`}
                      style={{ width: `${pair.gatesTotal > 0 ? (pair.gatesPassed / pair.gatesTotal) * 100 : 0}%` }}
                    />
                  </div>
                  {pair.gatesPassed < pair.gatesTotal && (
                    <p className="text-[9px] text-amber-400 mt-1.5 flex items-center gap-1">
                      <AlertTriangle className="w-2.5 h-2.5" />
                      {pair.gatesTotal - pair.gatesPassed} gate{pair.gatesTotal - pair.gatesPassed !== 1 ? 's' : ''} still require attention before discharge can be authorised
                    </p>
                  )}
                  <button
                    onClick={() => navigate('/effluent')}
                    className="mt-2 text-[10px] text-teal-400 hover:text-teal-300 font-medium flex items-center gap-1"
                  >
                    Open discharge gates <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              )}

              {/* Discharge authorisation status */}
              <div className={`rounded-xl border p-3 flex items-center gap-3 ${
                overallPass
                  ? 'border-emerald-500/30 bg-emerald-900/10'
                  : overallFail
                  ? 'border-rose-500/30 bg-rose-900/10'
                  : 'border-slate-800/40 bg-slate-800/10'
              }`}>
                {overallPass ? <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                  : overallFail ? <ShieldX className="w-5 h-5 text-rose-400 shrink-0" />
                  : <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />}
                <div>
                  <p className={`text-[11px] font-bold ${overallPass ? 'text-emerald-400' : overallFail ? 'text-rose-400' : 'text-amber-400'}`}>
                    {overallPass ? 'DISCHARGE AUTHORISED — all conditions met'
                      : overallFail ? 'DISCHARGE BLOCKED — conditions not met'
                      : 'PENDING — complete all steps before discharge'}
                  </p>
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    {overallPass
                      ? 'All effluent parameters within consent limits and all discharge gate checks passed'
                      : overallFail
                      ? 'One or more effluent parameters exceed consent limits or discharge gate checks incomplete'
                      : 'Record and link effluent sample, then complete discharge gate checks'}
                  </p>
                </div>
              </div>

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ComplianceGatewayPage() {
  const navigate = useNavigate();
  const { currentCustomer, activeStandard } = useSession();
  const { user } = useAuth();
  const [activeView, setActiveView] = useState<'workflow' | 'pairs'>('workflow');
  const [refreshKey, setRefreshKey] = useState(0);

  const standard = (currentCustomer as any)?.standard || activeStandard || 'UK_EA';
  const stdObj = ALL_STANDARDS.find(s => s.code === standard);

  const allSamples = useMemo(() => {
    void refreshKey; // subscribe to refresh
    return getAllLocalSamples();
  }, [refreshKey]);

  const pairs = useMemo(() =>
    buildPairs(allSamples, currentCustomer?.id || null, standard),
    [allSamples, currentCustomer, standard]
  );

  const pairsComplete   = pairs.filter(p => p.effluent && p.allParamsPass === true && p.gatesPassed === p.gatesTotal && p.gatesTotal > 0);
  const pairsIncomplete = pairs.filter(p => !p.effluent);
  const pairsFailing    = pairs.filter(p => p.allParamsPass === false);

  return (
    <div className="space-y-5 max-w-[1440px] mx-auto">

      <PageHeader
        icon={ShieldCheck}
        title="Discharge Compliance Gateway"
        subtitle="Evaluate effluent samples against regulatory discharge limits"
        iconColor="text-emerald-400"
        badge={
          <div className="flex items-center gap-2 flex-wrap">
            {stdObj && (
              <span className="text-xs px-2.5 py-1.5 rounded-lg font-bold bg-blue-500/10 text-blue-300 border border-blue-500/20">
                {stdObj.shortName}
              </span>
            )}
            {currentCustomer && (
              <span className="text-xs px-2.5 py-1.5 rounded-lg bg-slate-800 text-slate-400 border border-slate-700 flex items-center gap-1">
                <Building2 className="w-3 h-3" /> {currentCustomer.name}
              </span>
            )}
          </div>
        }
        actions={
          <button
            onClick={() => setRefreshKey(k => k + 1)}
            className="p-2 rounded-lg bg-slate-800/80 border border-slate-800/60 text-slate-400 hover:text-white hover:border-slate-600 transition-all"
            title="Refresh data"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        }
      />

      {/* ── Summary KPIs ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Sample Pairs', value: pairs.length, color: 'text-white', bg: 'bg-slate-800/40', border: 'border-slate-800/40', icon: Layers },
          { label: 'Discharge Ready', value: pairsComplete.length, color: 'text-emerald-400', bg: 'bg-emerald-500/5', border: 'border-emerald-500/20', icon: CheckCircle2 },
          { label: 'Awaiting Effluent', value: pairsIncomplete.length, color: 'text-amber-400', bg: 'bg-amber-500/5', border: 'border-amber-500/20', icon: AlertTriangle },
          { label: 'Non-Compliant', value: pairsFailing.length, color: 'text-rose-400', bg: 'bg-rose-500/5', border: 'border-rose-500/20', icon: ShieldX },
        ].map(k => (
          <div key={k.label} className={`rounded-xl border ${k.border} ${k.bg} p-3 flex items-center gap-3`}>
            <k.icon className={`w-5 h-5 ${k.color} shrink-0`} />
            <div>
              <p className={`text-xl font-black ${k.color}`}>{k.value}</p>
              <p className="text-[9px] text-slate-500 font-medium">{k.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Regulatory notice ── */}
      <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 px-4 py-3 flex items-start gap-3">
        <Info className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
        <div>
          <p className="text-[11px] font-bold text-blue-300 mb-0.5">Regulatory Requirement — Chain of Custody</p>
          <p className="text-[10px] text-slate-400 leading-relaxed">
            All influent and effluent samples must be linked for full chain-of-custody traceability as required by {stdObj?.name || 'the applicable regulatory standard'}.
            Discharge is only authorised when: (1) a compliant effluent sample is linked to the corresponding influent, and (2) all discharge gate checks are recorded as COMPLIANT by a named, authorised operator.
            Records must be retained for a minimum of {standard === 'US_EPA' ? '3 years (40 CFR 122.41)' : standard === 'EU_UWWTD' ? '5 years (Art.15 UWWTD)' : '5 years (EA Monitoring Certificate)'}.
          </p>
        </div>
      </div>

      {/* ── View toggle ── */}
      <div className="flex items-center gap-1 bg-slate-800/60 border border-slate-800/50 rounded-xl p-1 w-fit">
        <button
          onClick={() => setActiveView('workflow')}
          className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-[11px] font-medium transition-all ${
            activeView === 'workflow' ? 'bg-emerald-500 text-white font-bold' : 'text-slate-400 hover:text-slate-200'
          }`}
        >
          <List className="w-3.5 h-3.5" /> Workflow Guide
        </button>
        <button
          onClick={() => setActiveView('pairs')}
          className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-[11px] font-medium transition-all ${
            activeView === 'pairs' ? 'bg-rb-brand text-rb-surface font-bold' : 'text-slate-400 hover:text-slate-200'
          }`}
        >
          <Link2 className="w-3.5 h-3.5" /> Sample Pairs
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
            activeView === 'pairs' ? 'bg-rb-surface/30 text-rb-surface' : 'bg-slate-700 text-slate-400'
          }`}>{pairs.length}</span>
        </button>
      </div>

      {/* ── Workflow Guide ── */}
      {activeView === 'workflow' && (
        <div className="space-y-0">
          <p className="text-[10px] text-slate-500 mb-4 flex items-center gap-1.5">
            <BookOpen className="w-3 h-3" />
            Follow these 5 steps in order for every discharge event. Click each step button to navigate to the relevant page.
          </p>
          {WORKFLOW_STEPS.map((step, i) => (
            <WorkflowStepCard key={step.step} step={step} isLast={i === WORKFLOW_STEPS.length - 1} />
          ))}
        </div>
      )}

      {/* ── Sample Pairs ── */}
      {activeView === 'pairs' && (
        <div className="space-y-3">
          {!currentCustomer && (
            <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-3 flex items-center gap-3">
              <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
              <div>
                <p className="text-[11px] font-bold text-amber-400">No customer selected</p>
                <p className="text-[10px] text-slate-500">
                  Select a customer from the Customers page to see their sample pairs and compliance status.
                </p>
              </div>
              <button onClick={() => navigate('/customers')}
                className="ml-auto shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-all">
                <Building2 className="w-3 h-3" /> Select Customer
              </button>
            </div>
          )}

          {pairs.length === 0 ? (
            <div className="rb-card p-10 text-center">
              <Link2 className="w-10 h-10 text-slate-400 mx-auto mb-3" />
              <p className="text-sm font-semibold text-slate-400 mb-1">No influent samples found</p>
              <p className="text-xs text-slate-600 max-w-sm mx-auto mb-4">
                Start by recording an influent sample for {currentCustomer?.name || 'the selected customer'}.
                Each influent sample will appear here as a compliance pair once an effluent sample is linked.
              </p>
              <button onClick={() => navigate('/influent-entry')}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 hover:bg-cyan-500/20 transition-all">
                <Droplets className="w-3.5 h-3.5" /> Record First Influent Sample
              </button>
            </div>
          ) : (
            <>
              <p className="text-[10px] text-slate-600">
                {pairs.length} influent record{pairs.length !== 1 ? 's' : ''} — click any row to expand compliance detail.
                {pairsIncomplete.length > 0 && (
                  <span className="text-amber-500 ml-2">{pairsIncomplete.length} awaiting effluent sample.</span>
                )}
              </p>
              {pairs.map(pair => (
                <PairCard key={pair.influent.id} pair={pair} standard={standard} />
              ))}
            </>
          )}

          {/* Quick action buttons */}
          <div className="flex items-center gap-2 flex-wrap pt-2">
            <button onClick={() => navigate('/influent-entry')}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-bold bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 hover:bg-cyan-500/20 transition-all">
              <Droplets className="w-3.5 h-3.5" /> Add Influent Sample
            </button>
            <button onClick={() => navigate('/effluent-entry')}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-bold bg-rb-brand/10 text-rb-brand border border-rb-brand/20 hover:bg-rb-brand/20 transition-all">
              <Beaker className="w-3.5 h-3.5" /> Add Effluent Sample
            </button>
            <button onClick={() => navigate('/effluent')}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 transition-all">
              <ShieldCheck className="w-3.5 h-3.5" /> Discharge Gates
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
